/**
 * One editable rule: field · operator · value · live count · remove. Ported
 * from the concept's `segments/RuleRow.jsx`, on the real catalogue.
 *
 * ## THE COUNT IS ONE OF FOUR THINGS, AND ONLY ONE OF THEM IS A NUMBER
 *
 * - the rule is incomplete → "needs a value", amber, dashed border. Not 0: a
 *   rule nobody finished has matched nobody only in the sense that it has not
 *   been asked.
 * - the catalogue objects (unknown field, a date waiting for its format, an
 *   operator the type does not take) → the objection's own sentence, under the
 *   row, and no count. The API would refuse the tree, so there is nothing to
 *   count.
 * - the preview has not answered yet → `NoFigure` "awaiting", a dash with a
 *   spoken meaning.
 * - the preview answered → the number of people this rule matches ON ITS OWN,
 *   ignoring its siblings — which is what the concept's count meant, and the
 *   sentence a screen reader hears says so.
 *
 * The row is the Flow builder's condition row too (slice 69 imports it from
 * `components/rules`), so it takes the catalogue as props and owns no fetching.
 * A row whose field the catalogue no longer has still renders — the key in a
 * "Missing" chip, every operator of its last-known type — so a rule over a
 * column whose upload was deleted can be read and removed rather than vanishing
 * from the screen while still sitting in the saved tree.
 */
import {
  OPERATORS_BY_TYPE,
  operatorLabel,
  type Field,
  type FieldDateFormat,
  type FieldType,
  type RuleOperator,
} from '@wend/shared';
import { CoverageMeter, NoFigure, cn } from '@wend/ui';
import { X } from 'lucide-react';

import { RULE_REMOVE_BUTTON_CLASS, RULE_SELECT_CLASS } from './controls';
import { FieldPicker, coveragePercent } from './FieldPicker';
import { RuleValueEditor } from './RuleValueEditors';
import { reoperateRule, retargetRule, toLeaf, type DraftRule } from './rule-model';

const EN_IN = new Intl.NumberFormat('en-IN');

/** Below this share of people knowing a field, the row says how many cannot match. */
export const LOW_COVERAGE = 0.85;

/** Operators that are about whether a value exists, so a gap in coverage is their point, not a caveat. */
const PRESENCE_OPERATORS: readonly RuleOperator[] = ['known', 'unknown', 'not_in_last', 'never'];

export interface RuleRowProps {
  readonly rule: DraftRule;
  /** Every field the picker offers (normally the catalogue less hidden ones). */
  readonly fields: readonly Field[];
  /** This rule's own count from the preview; `undefined` while none has arrived. */
  readonly count: number | undefined;
  /** The preview is re-counting: the last count stays, dimmed. */
  readonly counting?: boolean;
  /** The catalogue's objection, as a sentence. */
  readonly issue?: string;
  readonly onChange: (rule: DraftRule) => void;
  readonly onRemove: () => void;
  readonly onChooseDateFormat?: (key: string, format: FieldDateFormat) => Promise<void>;
  /** Hide the count slot entirely — a Flow condition shows its counts elsewhere. */
  readonly showCount?: boolean;
  readonly disabled?: boolean;
}

/** "Age is at least 25" — the row's accessible description of itself. */
export function describeRule(rule: DraftRule, field: Pick<Field, 'label' | 'type'> | undefined): string {
  const type: FieldType = field?.type ?? 'text';
  return `${field?.label ?? rule.field} ${operatorLabel(rule.op, type).label}`;
}

export function RuleRow({
  rule,
  fields,
  count,
  counting = false,
  issue,
  onChange,
  onRemove,
  onChooseDateFormat,
  showCount = true,
  disabled = false,
}: RuleRowProps) {
  const field = fields.find((candidate) => candidate.key === rule.field);
  const type: FieldType = field?.type ?? guessType(rule.op);
  const derivedFrom = field?.origin.kind === 'derived' ? field.origin.sourceField : null;
  const derivedFromLabel = derivedFrom === null ? null : (fields.find((candidate) => candidate.key === derivedFrom)?.label ?? derivedFrom);
  const complete = toLeaf(rule) !== null;
  const operators = OPERATORS_BY_TYPE[type].includes(rule.op) ? OPERATORS_BY_TYPE[type] : [rule.op, ...OPERATORS_BY_TYPE[type]];
  const lowCoverage =
    field !== undefined && field.coverage !== null && field.coverage < LOW_COVERAGE && !PRESENCE_OPERATORS.includes(rule.op);
  const unmatched = field !== undefined && field.coverage !== null ? Math.round((1 - field.coverage) * 100) : null;
  const description = describeRule(rule, field);

  return (
    <div
      data-rule-id={rule.id}
      data-rule-state={issue !== undefined ? 'issue' : complete ? 'complete' : 'incomplete'}
      className={cn(
        'rounded-sm border bg-surface-2 py-2 pr-2 pl-2.5 transition-colors',
        complete && issue === undefined ? 'border-line-strong hover:border-white/25' : 'border-dashed border-warn/50',
      )}
    >
      <div className="flex flex-wrap items-center gap-[7px]">
        <FieldPicker
          fields={fields}
          value={rule.field}
          disabled={disabled}
          label="Rule field"
          onChooseDateFormat={onChooseDateFormat}
          onChange={(key) => {
            const next = fields.find((candidate) => candidate.key === key);
            if (next !== undefined) onChange(retargetRule(rule, next));
          }}
        />
        <select
          className={cn(RULE_SELECT_CLASS, 'text-muted')}
          value={rule.op}
          aria-label={`Operator for ${field?.label ?? rule.field}`}
          disabled={disabled}
          onChange={(event) => onChange(reoperateRule(rule, event.target.value as RuleOperator, field))}
        >
          {operators.map((op) => (
            <option key={op} value={op}>
              {operatorLabel(op, type).label}
            </option>
          ))}
        </select>
        <RuleValueEditor
          op={rule.op}
          type={type}
          field={field}
          value={rule.value}
          disabled={disabled}
          onChange={(value) => onChange({ ...rule, value })}
        />
        <span className="ml-auto inline-flex items-center gap-1">
          {showCount && (
            <span
              data-role="rule-count"
              className={cn(
                'whitespace-nowrap rounded-full border px-2.5 py-0.5 font-mono text-[0.72rem] tabular-nums',
                complete && issue === undefined ? 'border-line bg-white/5 text-muted' : 'border-warn/35 text-warn',
                counting && 'opacity-60',
              )}
            >
              {issue !== undefined ? (
                'cannot count'
              ) : !complete ? (
                'needs a value'
              ) : count === undefined ? (
                <NoFigure state="awaiting" meaning="Counting this rule" />
              ) : (
                <>
                  <span aria-hidden="true">{EN_IN.format(count)}</span>
                  <span className="sr-only">{`${EN_IN.format(count)} people match this rule on its own`}</span>
                </>
              )}
            </span>
          )}
          <button type="button" className={RULE_REMOVE_BUTTON_CLASS} aria-label={`Remove rule: ${description}`} onClick={onRemove} disabled={disabled}>
            <X aria-hidden="true" className="size-4" />
          </button>
        </span>
      </div>

      {issue !== undefined && (
        <p role="note" className="mt-1.5 border-t border-line pt-1.5 text-[0.76rem] text-warn">
          {issue}
        </p>
      )}

      {issue === undefined && (derivedFromLabel !== null || lowCoverage) && (
        <div className="mt-[7px] flex flex-wrap items-center gap-x-3.5 gap-y-1.5 border-t border-line pt-[7px] text-[0.74rem] text-muted">
          {derivedFromLabel !== null && (
            <span className="inline-flex items-center gap-1.5 font-mono text-[0.7rem] text-brand-ink">
              <span className="rounded-xs border border-brand-b/40 bg-brand-a/22 px-1.5 py-px text-[0.6rem] uppercase tracking-[0.1em]">derived</span>
              from {derivedFromLabel}
            </span>
          )}
          {lowCoverage && field !== undefined && (
            <span className="ml-auto inline-flex flex-wrap items-center gap-1.5 text-warn">
              <CoverageMeter percent={coveragePercent(field)} basis="known" compact />
              {unmatched !== null && <span>{`${String(unmatched)}% of people have no ${field.label}, so they cannot match this rule.`}</span>}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/** For a rule over a field the catalogue lost: the type its operator implies, so the operator list still makes sense. */
function guessType(op: RuleOperator): FieldType {
  for (const type of ['text', 'number', 'date', 'event', 'boolean'] as const) {
    if (OPERATORS_BY_TYPE[type].includes(op)) return type;
  }
  return 'text';
}
